import _ from 'underscore';

import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';

import { ILeafFilterClause } from '../../spacesPlatform/models/IFilterClause';
import { IListWithTokenFetcher } from '../fetchers/IListWithTokenFetcher';
import { ListResultWithToken } from '../fetchers/ListResultWithToken';
import { IPageWithToken } from '../serverApi/types/IPageWithToken';
import { formatFilterClauses } from './ResourceQuery';

/**
 * This is the resource query used for queries using the next page token instead of the standard paging used by other VizPortal APIs.
 */
 export class ResourceQueryWithPagingToken<T> {

    private filterClauses: ILeafFilterClause[] = [];
    private order: IOrderClause[] = [];
    private extraParams: any = {};

    constructor(private readonly fetcher: IListWithTokenFetcher<T>) {
        this.fetcher = fetcher;
    }

    // caller should not alter the returned array (this should return ReadonlyArray)
    public getFilters(): ILeafFilterClause[] {
        return this.filterClauses;
    }

    // caller should not alter the returned array (this should return ReadonlyArray)
    public getOrder(): IOrderClause[] {
        return this.order;
    }

    // caller should not alter the returned object
    public getExtraParams() {
        return this.extraParams;
    }

    public getPage(token: string, size: number): Promise<ListResultWithToken<T>> {
      return this.fetcher.fetchList(_.extend({
          filter: formatFilterClauses(this.getFilters()),
          order: this.getOrder().length > 0 ? this.getOrder() : undefined,
          pageWithToken: getPageWithToken(token, size)
        }, this.getExtraParams()));
    }

    public getFetcher() {
      return this.fetcher;
    }

    public withFilters(filterClauses: ILeafFilterClause[]): this {
      return this.clone().setFilterClauses(filterClauses);
    }

    public addFilter(filterClause: ILeafFilterClause): this {
      return this.withFilters((this.filterClauses || []).concat([filterClause]));
    }

    public withOrder(order: IOrderClause[]): this {
      return this.clone().setOrder(order);
    }

    private setFilterClauses(filterClauses: ILeafFilterClause[]): this {
      this.filterClauses = filterClauses;
      return this;
    }

    private setOrder(order: IOrderClause[]): this {
      this.order = order;
      return this;
    }

    public getFirst(): Promise<T> {
      return this.getPage(null, 1).then((result: ListResultWithToken<T>) => {
          if (result.items) {
            return result.items[0];
          } else {
            return null;
          }
        }
      );
    }

    public getCount(): Promise<number> {
      return this.getPage(null, 0).then((slice: ListResultWithToken<T>) => slice.total);
    }

    private clone(): this {
      return _.clone(this);
    }
  }

export function getPageWithToken(token: string, size: number): IPageWithToken {
    return { token: token, size: size };
}
